// 言語の解決と、UI の共通文言。
// 表示言語は「URL の ?lang=」だけで決まる（保存もクッキーも持たない）。SSR と
// クライアントが同じ URL から同じ言語を導けるので、Worker が返す HTML と
// main.ts が描く DOM がずれない。切り替えはページ遷移で行う（言語は 1 ページの中で変わらない）。
// 指標・プリセット・品質フラグ等、それぞれの表に属する文言は各モジュール側が持つ。

export type Lang = "ja" | "en";

export const LANGS: readonly Lang[] = ["ja", "en"];
export const DEFAULT_LANG: Lang = "ja";
export const LANG_PARAM = "lang";

/** 言語切り替えボタンに出す、その言語自身での表記。 */
export const LANG_NAMES: Record<Lang, string> = { ja: "日本語", en: "English" };

export function isLang(value: string | null | undefined): value is Lang {
  return value === "ja" || value === "en";
}

export function otherLang(lang: Lang): Lang {
  return lang === "ja" ? "en" : "ja";
}

export function langFromParams(params: URLSearchParams): Lang | null {
  const raw = params.get(LANG_PARAM);
  return isLang(raw) ? raw : null;
}

/**
 * Accept-Language の先頭から、対応言語に最初に当たったものを採る。
 * q 値は「ja;q=0.9, en;q=0.8」のように並び順と一致するのが普通なので、重み付けはせず順に見る。
 */
export function langFromAcceptLanguage(header: string | null | undefined): Lang | null {
  if (!header) return null;
  for (const part of header.split(",")) {
    const tag = part.split(";")[0]?.trim().toLowerCase() ?? "";
    if (tag === "*") continue;
    const primary = tag.split("-")[0];
    if (primary === "ja" || primary === "en") return primary;
  }
  return null;
}

/** URL の指定 > Accept-Language > 既定（日本語）。 */
export function resolveLang(params: URLSearchParams, acceptLanguage?: string | null): Lang {
  return langFromParams(params) ?? langFromAcceptLanguage(acceptLanguage) ?? DEFAULT_LANG;
}

/**
 * サイト内リンクに現在の言語を引き継ぐ。既定言語では何も足さないので、
 * 日本語の URL は今までと同じ形のまま（既存の被リンク・共有 URL が変わらない）。
 *
 * explicit を立てると既定言語でも lang= を書く。言語切り替えリンクと、
 * 利用者が一度 lang= を指定した後のリンクがこれにあたる。付けないと、
 * ブラウザの言語設定が英語の人は「日本語にする」を押しても Accept-Language で
 * 英語に戻されてしまう（=切り替えられない）。
 */
export function withLang(path: string, lang: Lang, explicit = false): string {
  if (lang === DEFAULT_LANG && !explicit) return path;
  const [base, hash] = splitHash(path);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${LANG_PARAM}=${lang}${hash}`;
}

function splitHash(path: string): [string, string] {
  const i = path.indexOf("#");
  return i === -1 ? [path, ""] : [path.slice(0, i), path.slice(i)];
}

export function numberLocale(lang: Lang): string {
  return lang === "ja" ? "ja-JP" : "en-US";
}

export function formatNumber(value: number, lang: Lang): string {
  return value.toLocaleString(numberLocale(lang));
}

/** 欠測を表す記号。言語に依らないが、「—」を各所に直書きしないための 1 箇所。 */
export const DASH = "—";

const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 市区町村ページの description に並べる、書式済みの指標値。 */
export interface MetricSummary {
  flood: string;
  tsunami: string;
  sediment: string;
  quake: string;
  landPrice: string;
  tempJan: string;
  tempAug: string;
  distTokyo: string;
}

const ja = {
  brand: "安住マップ",
  /** 「面積 x ・ 東京駅 y」のように短い項目を横に並べるときの区切り。 */
  sep: " ・ ",
  /** 読み上げ用に項目を列挙するときの区切り。 */
  listSep: "、",
  tagline: "災害・地価・気候でまちを比べる",
  siteTitle: "安住マップ | 災害リスク・地価・気候からまちを比較",
  siteDescription:
    "全国の市区町村を洪水・津波・土砂災害・地震確率・地価・気候・都心からの距離で絞り込み、地図で比較できます。",

  // --- 共通の導線 ---
  openMap: "地図で見る",
  guide: "使い方",
  sourcesShort: "出典",
  aboutSite: "安住マップについて",
  sourcesAndDisclaimer: "データの出典・免責事項",
  switchLanguage: "言語を切り替える",

  // --- トップバー ---
  searchLabel: "市区町村を検索",
  searchPlaceholder: "市区町村を検索",
  searchSuggestions: "検索候補",
  favorites: "候補",
  switchTheme: "テーマを切り替える",
  switchToLight: "ライトテーマに切り替える",
  switchToDark: "ダークテーマに切り替える",

  // --- パネル・シート・ドロワー ---
  filters: "絞り込み",
  results: "結果一覧",
  resultsShort: "結果",
  sheetToggle: "シートを開閉する",
  sheetTabs: "表示の切り替え",
  detailTitle: "市区町村の詳細",
  closeDetail: "詳細を閉じる",
  addToFavorites: "候補に追加",
  removeFromFavorites: "候補から外す",
  copyLink: "リンクをコピー",
  openAsPage: "ページとして開く",
  detailOf: (name: string) => `${name} の詳細`,

  // --- 絞り込みフォーム ---
  quickStart: "クイックスタート",
  mapColorAndSort: "地図の色 / 並び順",
  metrics: "指標",
  reset: "リセット",
  prefecture: "都道府県",
  allPrefectures: "すべて",
  hubDistance: "都心からの距離",
  hubStations: "基準の駅",
  hazardOverlay: "ハザードオーバーレイ",
  hazardOverlayNote: "出典: ハザードマップポータルサイト（透過 55%）",
  anyValue: "指定なし",
  filterTempJan: "1月の平均気温",
  filterTempAug: "8月の平均気温",
  filterPopulation: "総人口",
  tsunamiInlandShort: "指定なし地域",
  tsunamiInlandLabel: "津波浸水想定の指定が無い市区町村のみ",
  hazardLayer: {
    flood: "洪水",
    tsunami: "津波",
    "sediment-flow": "土石流",
    "sediment-steep": "急傾斜",
  } as Record<string, string>,
  anyValueFor: (label: string) => `${label}: 指定なし`,
  activeCount: (n: number) => `${n} 件`,
  hazardChipTitle: (label: string) => `${label}のハザードを地図に重ねる`,
  hubTitle: (label: string) => `${label}駅からの距離で絞り込む`,
  hubDistTitle: (label: string) => `都心から ${label}`,

  // --- 結果一覧 ---
  resultCount: (n: number) => `${formatNumber(n, "ja")} 件`,
  sortedBy: (label: string, betterWord = "低い") => `${label} が${betterWord}順`,
  sortedByTop: (label: string, n: number, betterWord = "低い") =>
    `${label} が${betterWord}順に 上位 ${formatNumber(n, "ja")} 件`,
  resultSummary: (count: string, sort: string) => `${count} ・ ${sort}`,
  unmeasuredNote: (n: number) => `うち未計測 ${formatNumber(n, "ja")} 件（絞り込んだ指標に値が無い市区町村）`,
  unmeasuredExcluded: (n: number) => `未計測 ${formatNumber(n, "ja")} 件を除外しています`,
  unmeasuredExclude: "未計測を除く",
  unmeasuredInclude: "未計測も含める",
  zeroResultLead: "条件に合う市区町村がありません。どれか 1 つ外すと次の件数になります。",
  zeroResultNoSingle:
    "条件に合う市区町村がありません。1 つ外しただけでは 0 件のままなので、まとめて解除してやり直してください。",
  zeroResultNoCondition: "条件に合う市区町村がありません。",
  zeroResultRelax: (label: string, n: number) => `${label} を外す → ${formatNumber(n, "ja")} 件`,
  zeroResultClearAll: "すべての条件を解除",
  loadFailed: "データの読み込みに失敗しました。しばらくしてから再度お試しください。",
  noData: "データなし",
  favoritesEmpty: "★ を押すとここに残ります。次に来たときも残っています。",
  favoritesFull: (max: number) => `候補は ${max} 件までです。古いものから外れました。`,
  favoriteToggle: (name: string, on: boolean) => `${name} を候補に${on ? "外す" : "追加"}`,
  linkCopied: "リンクをコピーしました",
  copyFailed: "コピーできませんでした",
  rankLabel: (word: string, percent: number, position: number, total: number) =>
    `全国で${word}方から上位 ${percent}%（${formatNumber(total, "ja")} 中 ${formatNumber(position, "ja")} 位）`,
  rankLabelShort: (word: string, percent: number) => `全国で${word}方から上位 ${percent}%`,
  pipSummary: (short: string, value: string, rank: string) => `${short} ${value}（${rank}）`,
  pipNoData: (short: string) => `${short} データなし`,

  // --- 詳細 ---
  source: "出典",
  showSource: "出典を表示",
  sourceMeta: (publisher: string, date: string) => `${publisher} ・ 取得日 ${date}`,
  staleNote: "⚠ 取得から180日超が経過しており、データが古い可能性があります。",
  disasterRisk: "災害リスク",
  tileFlood: "洪水浸水想定 面積比",
  tileTsunami: "津波浸水想定 面積比",
  tileSediment: "土砂災害警戒区域 面積比",
  tileQuake: "30年以内 震度6弱以上確率",
  notDesignated: "指定なし",

  // --- 現住地との比較 ---
  /** 詳細ページ先頭の案内。まだ現住地が無いとき。 */
  homeIntro: "現住地を設定すると、各指標を今住んでいる町と並べて見られます。",
  /** 現住地が別の町に設定されているとき。 */
  homeCurrentIs: (name: string) => `現住地: ${name}`,
  /** 開いている町そのものが現住地のとき。 */
  homeIsThisTown: "この町が現住地です",
  homeSet: "この町を現住地にする",
  homeClear: "現住地を解除",
  /** 各指標に添える 1 行。value は指標ごとのフォーマット済みの文字列。 */
  homeCompare: (name: string, value: string) => `現住地 ${name}: ${value}`,
  hazardNote:
    "値は市区町村全体の面積比です。個別地点のリスクはハザードマップポータルサイトで確認してください。",
  hazardNoteLink: "この地点を重ねるハザードマップで見る",
  landPriceHeading: "住宅地 地価（中央値）",
  landPriceSample: (n: number) => `標本 ${n} 件`,
  landPriceLowSampleRank: (minSample: number) => `標本が ${minSample} 件未満のため、全国順位は出していません`,
  climateHeading: "気候",
  climateChartLabel: "月別の平均気温",
  monthTemp: (month: number, temp: string) => `${month}月 ${temp}`,
  monthShort: (month: number) => `${month}月`,
  maxSnowDepth: "最深積雪",
  maxSnowDepthRow: "最深積雪（平年値）",
  populationHeading: "人口",
  facilitiesHeading: "暮らしの施設",
  rowPopulation: "総人口",
  rowAged65: "65歳以上の人口",
  rowFutureChange: "人口の増減（将来推計）",
  rowHospitals: "病院",
  rowClinics: "診療所",
  rowStores: "食料品店数",
  people: (n: number) => `${formatNumber(Math.round(n), "ja")} 人`,
  places: (n: number) => `${formatNumber(n, "ja")} か所`,
  stores: (n: number) => `${formatNumber(n, "ja")} 店`,
  densityNote: (n: number) => `人口密度 ${formatNumber(Math.round(n), "ja")} 人/km²`,
  agedRatioNote: (pct: string) => `高齢化率 ${pct}%`,
  futureNote: (baseYear: number, base: string, futureYear: number, future: string) =>
    `${baseYear}年 ${base} → ${futureYear}年 ${future}`,
  futureNoteNoYears: (base: string, future: string) => `現在 ${base} → 将来推計 ${future}`,
  perTenThousand: (x: string, unit: string) => `人口 1 万人あたり ${x} ${unit}`,
  unitPlaces: "か所",
  unitStores: "店",
  facilitiesNote:
    "食料品店数は経済センサス基礎調査（2024 年 6 月）の飲食料品小売業の事業所数で、スーパー・コンビニ・酒店・菓子店などを含む市区町村全体の合計です（雇用者のいない個人経営の店は含みません）。自宅から歩いて行ける距離にあるかは別途ご確認ください。病院は入院設備のある医療機関、診療所はかかりつけ医などの一般診療所（歯科を除く）です。",
  distanceHeading: "主要駅からの距離",
  greatCircleNote: "大圏距離（直線距離）です。",
  stationTokyo: "東京駅",
  stationOsaka: "大阪駅",
  stationNagoya: "名古屋駅",
  hubTokyo: "東京",
  hubOsaka: "大阪",
  hubNagoya: "名古屋",
  muniSubtitle: (areaKm2: string, distTokyo: string) => `面積 ${areaKm2} km² ・ 東京駅 ${distTokyo} km`,
  backToMap: "地図でこの市区町村を見る",
  hazardHeading: (name: string) => `${name}のハザードマップ・災害リスク`,
  muniPageTitle: (name: string) => `${name}のハザードマップ・災害リスク・地価 | 安住マップ`,
  /** 実データを並べた description。全角込みで概ね 160 文字に収まる構成にしている。 */
  muniPageDescription: (name: string, v: MetricSummary) =>
    `${name}のハザードマップ・災害リスク・地価データ。` +
    `水害（洪水浸水想定 面積比 ${v.flood}）、津波 ${v.tsunami}、土砂災害警戒区域 ${v.sediment}、` +
    `30年以内震度6弱以上 ${v.quake}、住宅地地価 中央値 ${v.landPrice}、` +
    `1月 ${v.tempJan}・8月 ${v.tempAug}、東京駅 ${v.distTokyo}。`,
  temporaryError: "一時的なエラーが発生しました",

  // --- パンくず・近隣・都道府県ハブ・404（SSR ページ） ---
  ogLocale: "ja_JP",
  breadcrumb: "パンくずリスト",
  crumbHome: "トップ",
  neighborsHeading: "近隣の市区町村",
  neighborsNote: "役場どうしの大圏距離（直線距離）が近い順です。",
  toPrefList: (pref: string) => `${pref}の市区町村一覧へ`,
  prefPageTitle: (pref: string) => `${pref}のハザードマップ・災害リスク・地価比較（市区町村一覧） | 安住マップ`,
  prefHeading: (pref: string, count: number) => `${pref}の市区町村一覧（${count} 件）`,
  prefIntro: (pref: string, count: number) =>
    `${pref}の ${count} 市区町村を、洪水・津波・土砂災害・30年以内の地震確率・住宅地の地価・気候の生値で並べています。市区町村名から詳細ページに進むと、全指標・全国順位・出典を確認できます。`,
  prefPageDescription: (pref: string, count: number) =>
    `${pref}の全 ${count} 市区町村のハザードマップ（洪水・津波・土砂災害・地震確率）、住宅地地価、気候データを比較できる一覧です。市区町村ごとの詳細ページで全国順位と出典も確認できます。`,
  prefColumnMuni: "市区町村",
  prefViewOnMap: (pref: string) => `地図で${pref}を見る`,
  prefBuiltNote: (builtAt: string) =>
    `ビルド日時 ${builtAt}。値の出典は各市区町村ページの「出典」から確認できます。`,
  findByPref: "都道府県から探す",
  notFoundHeading: "ページが見つかりません",
  notFoundLead:
    "お探しのページは存在しないか、URL が変更された可能性があります。地図から探すか、都道府県の一覧から辿ってください。",
  notFoundSearchMap: "地図で探す",
  notFoundPageTitle: "ページが見つかりません | 安住マップ",
  notFoundDescription:
    "お探しのページは見つかりませんでした。都道府県の一覧または地図から市区町村を探せます。",
  topSeoAria: "安住マップについて",
  topSeoH1: "安住マップ｜災害リスク・地価・気候でまちを比べる",
  topSeoP1: (total: string) =>
    `全国 ${total} 市区町村について、洪水浸水想定・津波浸水想定・土砂災害警戒区域の面積比、30年以内に震度6弱以上となる確率、住宅地の地価中央値、月別平均気温と最深積雪、東京駅・大阪駅・名古屋駅からの距離を並べています。`,
  topSeoP2:
    "数値はすべて国土数値情報・ハザードマップポータルサイト・気象庁・J-SHIS などの公開データをビルド時に集計した生値です。独自の合成スコアやランキングは持たず、指標を混ぜずにそのまま比べられるようにしています。",
  topSeoP3Before: "欠測は「—」として残し、その理由を品質フラグで示します。詳しい出典と免責事項は",
  topSeoP3After: "を参照してください。",

  // --- 指標別ランキング（/rank, /rank/{metric}, /rank/{metric}/{pref}） ---
  crumbRank: "ランキング",
  rankIndexPageTitle: "市区町村ランキング（指標別・全国） | 安住マップ",
  rankIndexHeading: "市区町村ランキング（指標別）",
  rankIndexDescription:
    "洪水・津波・土砂災害・地震確率・地価・気候・人口・食料品店数・都心からの距離の 12 指標で、全国の市区町村を並べたランキングの一覧です。",
  rankIndexIntro:
    "指標ごとに、全国の市区町村を望ましい方から並べています。指標を選ぶと全国順位と都道府県別の順位を見られます。合成スコアは使わず、生値だけで並べています。",
  rankPageTitle: (rankName: string) => `${rankName}市区町村ランキング（全国） | 安住マップ`,
  rankHeading: (rankName: string) => `${rankName}市区町村ランキング（全国）`,
  rankPrefPageTitle: (pref: string, rankName: string) =>
    `${pref}で${rankName}市区町村ランキング | 安住マップ`,
  rankPrefHeading: (pref: string, rankName: string) =>
    `${pref}で${rankName}市区町村ランキング`,
  /** description の先頭に置く上位 3 件。値まで入れてページごとに固有の文にする。 */
  rankTopEntry: (position: number, name: string, value: string) => `${position} 位 ${name}（${value}）`,
  rankPageDescription: (top: string, count: number, rankName: string) =>
    `${top ? `${top}。` : ""}全国 ${formatNumber(count, "ja")} 市区町村の${rankName}順ランキング。`,
  rankPrefPageDescription: (pref: string, top: string, count: number, rankName: string) =>
    `${top ? `${top}。` : ""}${pref}の ${formatNumber(count, "ja")} 市区町村の${rankName}順ランキング。`,
  rankIntro: (count: number, rankName: string) =>
    `全国 ${formatNumber(count, "ja")} 市区町村を、${rankName}順に並べています。`,
  rankPrefIntro: (pref: string, count: number, rankName: string) =>
    `${pref}の ${formatNumber(count, "ja")} 市区町村を、${rankName}順に並べています。`,
  rankTopNote: (limit: number) => `このページには上位 ${formatNumber(limit, "ja")} 件を表示しています。`,
  rankExcludedNote: (n: number) => `未計測 ${formatNumber(n, "ja")} 件は除外しています。`,
  rankLandPriceNote: (minSample: number) =>
    `住宅地の地価は、標本が ${minSample} 件未満の市区町村を順位から除いています（中央値が安定しないため）。`,
  rankColumnPosition: "順位",
  rankColumnPref: "都道府県",
  rankColumnValue: "値",
  rankMetricLinkLabel: (label: string, betterWord: string) => `${label}が${betterWord}順`,
  rankByPref: "都道府県別に見る",
  rankInPrefHeading: (pref: string) => `${pref}の指標別ランキング`,
  rankEmpty: "この指標を計測できた市区町村がありません。",

  /** 表の前に置く要約。順位表だけでは伝わらない全国的な位置づけを、そのページのデータから計算した事実だけで示す。 */
  rankSummaryHeading: "データの概要",
  rankSummaryMedian: (value: string) => `全国の中央値は ${value} です。`,
  rankSummaryExtreme: (word: string, name: string, value: string) => `最も${word}のは ${name}（${value}）です。`,
  rankSummaryExtremeTied: (word: string, value: string, count: number) =>
    `最も${word}値は ${value} で、${formatNumber(count, "ja")} 市区町村が同じ値です。`,
  rankSummaryTopLimit: (limit: number, value: string) =>
    `この表の ${formatNumber(limit, "ja")} 位の値は ${value} です。`,
  rankPrefSummaryMedian: (pref: string, prefMedian: string, nationalMedian: string) =>
    `${pref}の中央値は ${prefMedian}、全国の中央値は ${nationalMedian} です。`,
  rankPrefSummaryBetterCount: (pref: string, total: number, better: number, betterWord: string) =>
    `${pref}の ${formatNumber(total, "ja")} 市区町村のうち、全国の中央値より${betterWord}のは ${formatNumber(better, "ja")} 市区町村です。`,

  // --- ガイド記事（/guide, /guide/{slug}） ---
  crumbGuide: "ガイド",
  guideIndexPageTitle: "移住先を選ぶためのガイド記事 | 安住マップ",
  guideIndexHeading: "ガイド記事",
  guideIndexDescription:
    "「東京から 150km 以内で災害リスクが低く地価が安い町」のような複数条件の絞り込みを、実データの表と読み方の解説にまとめたガイド記事の一覧です。",
  guideIndexIntro:
    "指標を 1 つずつ見ていても答えが出ない問いを、条件を重ねた表と、その数字の読み方の解説でまとめています。表と本文の数字は公開データの最新の集計から組み立てています。",
  guidePageTitle: (title: string) => `${title} | 安住マップ`,
  guideHowToRead: "この表の読み方",
  guideCaveats: "読むときの注意",
  guideRelatedPages: "関連ページ",
  guideRelatedGuides: "関連ガイド",
  guideEmpty: "条件に当てはまる市区町村がありませんでした。",
  guideColumnMedian: "中央値",
  guideColumnLowestMuni: "いちばん低い市区町村",
  guideColumnHighestMuni: "いちばん高い市区町村",
  guideColumnAkiyaListed: "空き家バンク公式 URL 収録数",
  guideColumnMuniCount: "市区町村数",
  guideColumnCoverage: "収録率",

  // --- 空き家バンク ---
  akiyaOfficial: (name: string) => `${name} 空き家バンク（公式）`,
  akiyaSearch: (name: string) => `${name} 空き家バンクを検索`,
  akiyaAthome: (name: string) => `${name} 空き家バンクの物件一覧（at home）`,
  akiyaAthomeNationwide: (name: string) => `at home 空き家バンク（${name}）`,
  akiyaNationwide: "全国版",
  akiyaUnlistedNote:
    "この自治体の公式空き家バンク URL は未収録です（国土交通省のリンク集に無いか、到達確認に失敗）。物件は次のリンクから探せます。",
  akiyaUnlistedTipBefore: "公式 URL をご存じの方は",
  akiyaUnlistedTipLink: "お問い合わせ",
  akiyaUnlistedTipAfter: "からお知らせください。",

  // --- /about ---
  aboutTitle: "データの出典・免責事項",
  aboutPageTitle: "データの出典・免責事項 | 安住マップ",
  aboutPageDescription: "安住マップが利用するデータの出典一覧と免責事項。",
  aboutDisclaimer:
    "本サイトは公開データを機械的に集計したものであり、正確性・最新性を保証しません。移住・不動産の意思決定は、必ず自治体・専門家への確認と現地調査を行ってください。",
  aboutHazardNote:
    "災害リスクの面積比は市区町村全体を対象にした概算値です。個別地点のリスクはハザードマップポータルサイト等で確認してください。",
  aboutAnalytics:
    "本サイトは Google アナリティクス（GA4）でアクセス状況を計測し、Google AdSense で広告を配信する場合があります。",
  aboutLanguageNote:
    "英語表示では UI と指標名だけを英語にしています。市区町村名と出典の名称は、原典と照合できるよう日本語のまま表示します。",
  aboutSourcesHeading: (count: number, builtAt: string) =>
    `出典一覧（${count} 市区町村、ビルド日時 ${builtAt}）`,
  aboutContactHeading: "運営者・お問い合わせ",
  aboutContactBody: "株式会社BUILD GEEKSが運営しています。データの誤りのご指摘やご連絡は、",
  aboutContactBodyAfter: "からお願いします。個人情報の扱いは",
  aboutContactBodyEnd: "に記載しています。",

  // --- /contact ---
  contactTitle: "お問い合わせ",
  contactPageTitle: "お問い合わせ | 安住マップ",
  contactPageDescription:
    "安住マップへのお問い合わせフォーム。データの訂正、自治体の空き家バンク公式 URL のご提供などを受け付けています。",
  contactIntro:
    "データの訂正、公式空き家バンク URL のご提供、その他のご連絡を受け付けています。いただいた内容には、いただいたメールアドレス宛に返信します。",
  contactPrivacyNoteBefore: "入力内容の扱いは",
  contactPrivacyNoteAfter: "を参照してください。",
  contactNameLabel: "お名前（任意）",
  contactEmailLabel: "メールアドレス（必須）",
  contactTopicLabel: "ご用件",
  contactTopicLabels: {
    correction: "データの訂正",
    "akiya-bank-url": "空き家バンク公式 URL の提供",
    other: "その他",
  },
  contactMessageLabel: "内容（必須）",
  contactMessageHint: "10 文字以上 4000 文字以内でご記入ください。",
  contactSubmit: "送信する",
  contactErrorsHeading: "入力内容をご確認ください",
  contactErrNameTooLong: "お名前は 100 文字以内で入力してください。",
  contactErrEmailRequired: "メールアドレスを入力してください。",
  contactErrEmailInvalid: "メールアドレスの形式が正しくありません。",
  contactErrEmailTooLong: "メールアドレスは 254 文字以内で入力してください。",
  contactErrTopicInvalid: "ご用件を選択してください。",
  contactErrMessageTooShort: "内容は 10 文字以上で入力してください。",
  contactErrMessageTooLong: "内容は 4000 文字以内で入力してください。",
  contactErrTurnstile: "認証に失敗しました。ページを開き直してもう一度お試しください。",
  contactThanksTitle: "送信しました",
  contactThanksBody:
    "お問い合わせを受け付けました。返信が必要な内容には、いただいたメールアドレス宛にご連絡します。",
  contactBackToTop: "トップへ戻る",
  contactUnavailableTitle: "お問い合わせを一時的に受け付けられません",
  contactUnavailableBody:
    "メール送信の設定が済んでいないため、現在お問い合わせを受け付けられません。時間をおいてお試しください。",
  contactRateLimitedTitle: "送信の回数が多すぎます",
  contactRateLimitedBody: "しばらく時間をおいてから、もう一度お試しください。",
  contactFailedTitle: "送信できませんでした",
  contactFailedBody:
    "メールの送信に失敗しました。お手数ですが、時間をおいてもう一度お試しください。",

  // --- /privacy ---
  privacyTitle: "プライバシーポリシー",
  privacyPageTitle: "プライバシーポリシー | 安住マップ",
  privacyPageDescription:
    "安住マップが取得する情報、その利用目的、第三者提供、オプトアウトの方法、保存期間について。",
  privacyUpdated: (date: string) => `改定日 ${date}`,
  privacyOperatorHeading: "運営者",
  privacyOperatorBody: "株式会社BUILD GEEKSが運営しています。ご連絡は",
  privacyOperatorBodyAfter: "からお願いします。",
  privacyCollectHeading: "取得する情報",
  privacyCollectGa:
    "Google アナリティクス（GA4）による cookie、端末・ブラウザの情報、閲覧したページ、IP アドレス。IP アドレスは Google 側で匿名化して扱われ、当サイトが個別の IP アドレスを閲覧することはありません。",
  privacyCollectAds: "Google AdSense が広告配信のために利用する cookie。",
  privacyCollectContact:
    "お問い合わせフォームに入力されたお名前・メールアドレス・ご用件・内容。",
  privacyCollectLocal:
    "「候補の保存」とテーマ設定はブラウザの localStorage に保存され、サーバーには送信されません。",
  privacyPurposeHeading: "利用目的",
  privacyPurposeBody:
    "サイトの改善（どのページや機能が使われているかの把握）と、お問い合わせへの対応のために利用します。",
  privacyThirdPartyHeading: "第三者提供",
  privacyThirdPartyBody:
    "アクセス解析と広告配信のため Google LLC（GA4・AdSense）を、お問い合わせメールの送信のため Resend, Inc. を利用しています。各社の取り扱いは次のポリシーを参照してください。",
  privacyLinkGooglePartner: "Google のサービスを使用するサイトやアプリから収集した情報の Google による使用",
  privacyLinkGoogleAds: "広告における Google のポリシー",
  privacyLinkResend: "Resend プライバシーポリシー",
  privacyOptOutHeading: "オプトアウト",
  privacyOptOutBody: "計測と広告のパーソナライズは、次のページから停止できます。",
  privacyLinkGaOptOut: "Google アナリティクス オプトアウト アドオン",
  privacyLinkAdsSettings: "広告設定",
  privacyRetentionHeading: "保存期間",
  privacyRetentionGa: "GA4 のイベントデータの保持期間は 14 か月です。",
  privacyRetentionContact:
    "お問い合わせのメールは、対応が完了してから 1 年以内に削除します。",
  privacyDisclaimerHeading: "免責事項",
  privacyDisclaimerBefore: "掲載データの正確性や免責事項については",
  privacyDisclaimerAfter: "に記載しています。",
  privacyPolicy: "プライバシーポリシー",
  contactNav: "お問い合わせ",
};

/** ja を唯一の形の定義とし、en に抜けがあればコンパイルで落とす。 */
export type Dict = typeof ja;

const en: Dict = {
  brand: "Settle Map",
  sep: " · ",
  listSep: ", ",
  tagline: "Compare Japanese towns by hazard, land price and climate",
  siteTitle: "Settle Map | Compare Japanese towns by hazard, land price and climate",
  siteDescription:
    "Filter every municipality in Japan by flood, tsunami, landslide, earthquake probability, land price, climate and distance from major cities, and compare them on one map.",

  openMap: "Open the map",
  guide: "Guide",
  sourcesShort: "Sources",
  aboutSite: "About Settle Map",
  sourcesAndDisclaimer: "Data sources & disclaimer",
  switchLanguage: "Switch language",

  searchLabel: "Search municipalities",
  searchPlaceholder: "Search municipalities",
  searchSuggestions: "Search suggestions",
  favorites: "Shortlist",
  switchTheme: "Switch theme",
  switchToLight: "Switch to the light theme",
  switchToDark: "Switch to the dark theme",

  filters: "Filters",
  results: "Results",
  resultsShort: "Results",
  sheetToggle: "Open or close the sheet",
  sheetTabs: "Switch view",
  detailTitle: "Municipality details",
  closeDetail: "Close details",
  addToFavorites: "Add to shortlist",
  removeFromFavorites: "Remove from shortlist",
  copyLink: "Copy link",
  openAsPage: "Open as a page",
  detailOf: (name: string) => `Details for ${name}`,

  quickStart: "Quick start",
  mapColorAndSort: "Map color / sort",
  metrics: "Metrics",
  reset: "Reset",
  prefecture: "Prefecture",
  allPrefectures: "All",
  hubDistance: "Distance from a major city",
  hubStations: "Reference station",
  hazardOverlay: "Hazard overlay",
  hazardOverlayNote: "Source: Hazard Map Portal (55% opacity)",
  anyValue: "Any",
  filterTempJan: "Average temperature in January",
  filterTempAug: "Average temperature in August",
  filterPopulation: "Population",
  tsunamiInlandShort: "None designated",
  tsunamiInlandLabel: "Only municipalities with no designated tsunami inundation area",
  hazardLayer: {
    flood: "Flood",
    tsunami: "Tsunami",
    "sediment-flow": "Debris flow",
    "sediment-steep": "Steep slope",
  },
  anyValueFor: (label: string) => `${label}: any`,
  activeCount: (n: number) => `${n} active`,
  hazardChipTitle: (label: string) => `Overlay the ${label} hazard on the map`,
  hubTitle: (label: string) => `Filter by distance from ${label} Station`,
  hubDistTitle: (label: string) => `Within ${label} of the city`,

  resultCount: (n: number) => `${formatNumber(n, "en")} results`,
  sortedBy: (label: string, betterWord = "lowest") => `${label}, ${betterWord} first`,
  sortedByTop: (label: string, n: number, betterWord = "lowest") =>
    `${label}, ${betterWord} first — top ${formatNumber(n, "en")}`,
  resultSummary: (count: string, sort: string) => `${count} · ${sort}`,
  unmeasuredNote: (n: number) => `${formatNumber(n, "en")} of these have no value for a filter you set`,
  unmeasuredExcluded: (n: number) => `${formatNumber(n, "en")} without data excluded`,
  unmeasuredExclude: "Exclude unmeasured",
  unmeasuredInclude: "Include unmeasured",
  zeroResultLead: "No municipality matches. Removing one of these gives:",
  zeroResultNoSingle:
    "No municipality matches. Removing a single condition still gives none, so clear them and start again.",
  zeroResultNoCondition: "No municipality matches.",
  zeroResultRelax: (label: string, n: number) => `Remove ${label} → ${formatNumber(n, "en")}`,
  zeroResultClearAll: "Clear all conditions",
  loadFailed: "Could not load the data. Please try again in a moment.",
  noData: "no data",
  favoritesEmpty: "Press ★ to keep a municipality here. It stays on your next visit.",
  favoritesFull: (max: number) => `The shortlist holds ${max} municipalities. The oldest one was dropped.`,
  favoriteToggle: (name: string, on: boolean) =>
    on ? `Remove ${name} from the shortlist` : `Add ${name} to the shortlist`,
  linkCopied: "Link copied",
  copyFailed: "Could not copy the link",
  rankLabel: (word: string, percent: number, position: number, total: number) =>
    `Top ${percent}% from the ${word} in Japan (${formatNumber(position, "en")} of ${formatNumber(total, "en")})`,
  rankLabelShort: (word: string, percent: number) => `Top ${percent}% from the ${word} in Japan`,
  pipSummary: (short: string, value: string, rank: string) => `${short} ${value} (${rank})`,
  pipNoData: (short: string) => `${short}: no data`,

  source: "Source",
  showSource: "Show the source",
  sourceMeta: (publisher: string, date: string) => `${publisher} · retrieved ${date}`,
  staleNote: "⚠ Retrieved more than 180 days ago, so this data may be out of date.",
  disasterRisk: "Disaster risk",
  tileFlood: "Flood inundation area ratio",
  tileTsunami: "Tsunami inundation area ratio",
  tileSediment: "Landslide alert area ratio",
  tileQuake: "Probability of shindo 6-lower or above in 30 years",
  notDesignated: "None",

  // --- Comparison with the home town ---
  homeIntro: "Set your home town to see every metric next to where you live now.",
  homeCurrentIs: (name: string) => `Home town: ${name}`,
  homeIsThisTown: "This is your home town",
  homeSet: "Set as my home town",
  homeClear: "Clear home town",
  homeCompare: (name: string, value: string) => `Home (${name}): ${value}`,
  hazardNote:
    "These values are area ratios for the whole municipality. Check the risk at a specific location on the Hazard Map Portal.",
  hazardNoteLink: "View this location on the Hazard Map Portal",
  landPriceHeading: "Residential land price (median)",
  landPriceSample: (n: number) => `${n} sample points`,
  landPriceLowSampleRank: (minSample: number) => `No national rank: fewer than ${minSample} sample points`,
  climateHeading: "Climate",
  climateChartLabel: "Monthly average temperature",
  monthTemp: (month: number, temp: string) => `${MONTHS_EN[month - 1] ?? month} ${temp}`,
  monthShort: (month: number) => MONTHS_EN[month - 1] ?? String(month),
  maxSnowDepth: "Max snow depth",
  maxSnowDepthRow: "Max snow depth (normal)",
  populationHeading: "Population",
  facilitiesHeading: "Everyday facilities",
  rowPopulation: "Population",
  rowAged65: "Population aged 65+",
  rowFutureChange: "Projected population change",
  rowHospitals: "Hospitals",
  rowClinics: "Clinics",
  rowStores: "Food stores",
  people: (n: number) => `${formatNumber(Math.round(n), "en")} people`,
  places: (n: number) => formatNumber(n, "en"),
  stores: (n: number) => formatNumber(n, "en"),
  densityNote: (n: number) => `Density ${formatNumber(Math.round(n), "en")} /km²`,
  agedRatioNote: (pct: string) => `${pct}% aged 65+`,
  futureNote: (baseYear: number, base: string, futureYear: number, future: string) =>
    `${baseYear}: ${base} → ${futureYear}: ${future}`,
  futureNoteNoYears: (base: string, future: string) => `Now ${base} → projected ${future}`,
  perTenThousand: (x: string, unit: string) => `${x} ${unit} per 10,000 people`,
  unitPlaces: "facilities",
  unitStores: "stores",
  facilitiesNote:
    "Food store counts come from the 2024 Economic Census (basic survey) count of food and beverage retail establishments, including supermarkets, convenience stores, liquor stores and confectioners, totaled for the whole municipality (sole proprietorships with no employees are excluded). Check walking distance separately. Hospitals have inpatient beds; clinics are general practices (dental clinics excluded).",
  distanceHeading: "Distance to major stations",
  greatCircleNote: "Great-circle (straight-line) distance.",
  stationTokyo: "Tokyo Station",
  stationOsaka: "Osaka Station",
  stationNagoya: "Nagoya Station",
  hubTokyo: "Tokyo",
  hubOsaka: "Osaka",
  hubNagoya: "Nagoya",
  muniSubtitle: (areaKm2: string, distTokyo: string) =>
    `Area ${areaKm2} km² · ${distTokyo} km from Tokyo Station`,
  backToMap: "View this municipality on the map",
  hazardHeading: (name: string) => `Hazard map and disaster risk in ${name}`,
  muniPageTitle: (name: string) => `${name}: hazard map, disaster risk and land price | Settle Map`,
  muniPageDescription: (name: string, v: MetricSummary) =>
    `Hazard map, disaster risk and land price data for ${name}. ` +
    `Flood inundation ${v.flood}, tsunami ${v.tsunami}, landslide alert area ${v.sediment}, ` +
    `shindo 6-lower or above in 30 years ${v.quake}, median residential land price ${v.landPrice}, ` +
    `January ${v.tempJan} / August ${v.tempAug}, ${v.distTokyo} from Tokyo Station.`,
  temporaryError: "A temporary error occurred",

  ogLocale: "en_US",
  breadcrumb: "Breadcrumb",
  crumbHome: "Home",
  neighborsHeading: "Nearby municipalities",
  neighborsNote: "Ordered by great-circle distance between town halls.",
  toPrefList: (pref: string) => `All municipalities in ${pref}`,
  prefPageTitle: (pref: string) => `Municipalities in ${pref}: hazard maps, disaster risk and land price | Settle Map`,
  prefHeading: (pref: string, count: number) => `Municipalities in ${pref} (${count})`,
  prefIntro: (pref: string, count: number) =>
    `The ${count} municipalities of ${pref}, listed with raw values for flood, tsunami and landslide areas, the 30-year earthquake probability, residential land price and climate. Open a municipality for every metric, its nationwide rank and its source.`,
  prefPageDescription: (pref: string, count: number) =>
    `Compare all ${count} municipalities of ${pref} by hazard maps (flood, tsunami, landslide, earthquake probability), residential land price and climate. Each municipality page also shows nationwide ranks and sources.`,
  prefColumnMuni: "Municipality",
  prefViewOnMap: (pref: string) => `View ${pref} on the map`,
  prefBuiltNote: (builtAt: string) =>
    `Built at ${builtAt}. Sources for each value are on the municipality pages, under “Source”.`,
  findByPref: "Browse by prefecture",
  notFoundHeading: "Page not found",
  notFoundLead:
    "This page does not exist, or its URL has changed. Try the map, or work down from the list of prefectures.",
  notFoundSearchMap: "Search on the map",
  notFoundPageTitle: "Page not found | Settle Map",
  notFoundDescription:
    "This page could not be found. You can find municipalities from the list of prefectures or on the map.",
  topSeoAria: "About Settle Map",
  topSeoH1: "Settle Map — compare Japanese towns by hazard, land price and climate",
  topSeoP1: (total: string) =>
    `For all ${total} municipalities in Japan: the area ratios of expected flood, tsunami and landslide zones, the probability of shindo 6-lower or above within 30 years, the median residential land price, monthly average temperatures and maximum snow depth, and the distance from Tokyo, Osaka and Nagoya stations.`,
  topSeoP2:
    "Every figure is a raw value aggregated at build time from public data — National Land Numerical Information, the Hazard Map Portal, the JMA and J-SHIS. There is no composite score and no ranking of our own, so the metrics can be compared without being mixed together.",
  topSeoP3Before:
    "Missing values stay as “—”, with a quality flag explaining why. For the full sources and disclaimer, see ",
  topSeoP3After: ".",

  crumbRank: "Rankings",
  rankIndexPageTitle: "Municipality rankings by metric, nationwide | Settle Map",
  rankIndexHeading: "Municipality rankings by metric",
  rankIndexDescription:
    "Rankings of every municipality in Japan across 12 metrics: flood, tsunami, landslide, earthquake probability, land price, climate, population, food stores and distance from major cities.",
  rankIndexIntro:
    "For each metric, every municipality in Japan ordered from the most favourable end. Pick a metric to see the nationwide ranking and the ranking within each prefecture. There is no composite score — only raw values.",
  rankPageTitle: (rankName: string) =>
    `Municipalities with the ${rankName} in Japan | Settle Map`,
  rankHeading: (rankName: string) => `Municipalities with the ${rankName} in Japan`,
  rankPrefPageTitle: (pref: string, rankName: string) =>
    `Municipalities with the ${rankName} in ${pref} | Settle Map`,
  rankPrefHeading: (pref: string, rankName: string) =>
    `Municipalities with the ${rankName} in ${pref}`,
  rankTopEntry: (position: number, name: string, value: string) => `#${position} ${name} (${value})`,
  rankPageDescription: (top: string, count: number, rankName: string) =>
    `${top ? `${top}. ` : ""}${formatNumber(count, "en")} municipalities in Japan ranked by ${rankName}.`,
  rankPrefPageDescription: (pref: string, top: string, count: number, rankName: string) =>
    `${top ? `${top}. ` : ""}${formatNumber(count, "en")} municipalities in ${pref} ranked by ${rankName}.`,
  rankIntro: (count: number, rankName: string) =>
    `${formatNumber(count, "en")} municipalities in Japan, ordered by ${rankName}.`,
  rankPrefIntro: (pref: string, count: number, rankName: string) =>
    `${formatNumber(count, "en")} municipalities in ${pref}, ordered by ${rankName}.`,
  rankTopNote: (limit: number) => `This page lists the top ${formatNumber(limit, "en")}.`,
  rankExcludedNote: (n: number) => `${formatNumber(n, "en")} municipalities without data are excluded.`,
  rankLandPriceNote: (minSample: number) =>
    `Municipalities with fewer than ${minSample} residential land price samples are left out of the ranking, because the median is unstable there.`,
  rankColumnPosition: "Rank",
  rankColumnPref: "Prefecture",
  rankColumnValue: "Value",
  rankMetricLinkLabel: (label: string, betterWord: string) => `${label}, ${betterWord} first`,
  rankByPref: "Browse by prefecture",
  rankInPrefHeading: (pref: string) => `Rankings within ${pref}`,
  rankEmpty: "No municipality has a value for this metric.",

  rankSummaryHeading: "Summary",
  rankSummaryMedian: (value: string) => `The nationwide median is ${value}.`,
  rankSummaryExtreme: (word: string, name: string, value: string) => `The ${word} is ${name} (${value}).`,
  rankSummaryExtremeTied: (word: string, value: string, count: number) =>
    `The ${word} value is ${value}, shared by ${formatNumber(count, "en")} municipalities.`,
  rankSummaryTopLimit: (limit: number, value: string) =>
    `The value at rank ${formatNumber(limit, "en")} on this table is ${value}.`,
  rankPrefSummaryMedian: (pref: string, prefMedian: string, nationalMedian: string) =>
    `The median in ${pref} is ${prefMedian}, compared with ${nationalMedian} nationwide.`,
  rankPrefSummaryBetterCount: (pref: string, total: number, better: number) =>
    `Of the ${formatNumber(total, "en")} municipalities in ${pref}, ${formatNumber(better, "en")} are on the better side of the nationwide median.`,

  crumbGuide: "Guides",
  guideIndexPageTitle: "Guides for choosing where to settle in Japan | Settle Map",
  guideIndexHeading: "Guides",
  guideIndexDescription:
    "Guides that answer multi-condition questions such as \"low-hazard, low-price towns within 150 km of Tokyo\" with a table built from the data and an explanation of how to read it.",
  guideIndexIntro:
    "Questions that no single metric answers, written up as a table of stacked conditions plus an explanation of what the numbers mean. The tables and the figures in the text come from the latest aggregation of the public data.",
  guidePageTitle: (title: string) => `${title} | Settle Map`,
  guideHowToRead: "How to read this table",
  guideCaveats: "What the numbers do not tell you",
  guideRelatedPages: "Related pages",
  guideRelatedGuides: "Related guides",
  guideEmpty: "No municipality matches these conditions.",
  guideColumnMedian: "Median",
  guideColumnLowestMuni: "Lowest municipality",
  guideColumnHighestMuni: "Highest municipality",
  guideColumnAkiyaListed: "Official akiya bank URLs",
  guideColumnMuniCount: "Municipalities",
  guideColumnCoverage: "Coverage",
  akiyaOfficial: (name: string) => `${name}: official akiya (vacant home) bank`,
  akiyaSearch: (name: string) => `Search for an akiya (vacant home) bank in ${name}`,
  akiyaAthome: (name: string) => `${name}: akiya (vacant home) listings on at home`,
  akiyaAthomeNationwide: (name: string) => `at home akiya bank (${name})`,
  akiyaNationwide: "Nationwide",
  akiyaUnlistedNote:
    "We have no official akiya (vacant home) bank URL for this municipality (it is missing from the MLIT link list, or the reachability check failed). You can still look for listings through the link below.",
  akiyaUnlistedTipBefore: "If you know the official URL, please tell us through the ",
  akiyaUnlistedTipLink: "contact form",
  akiyaUnlistedTipAfter: ".",

  aboutTitle: "Data sources & disclaimer",
  aboutPageTitle: "Data sources & disclaimer | Settle Map",
  aboutPageDescription: "The data sources Settle Map builds on, and its disclaimer.",
  aboutDisclaimer:
    "This site aggregates public data mechanically and does not guarantee its accuracy or timeliness. Before making any relocation or property decision, confirm the figures with the municipality and a professional, and visit the site yourself.",
  aboutHazardNote:
    "Hazard area ratios are rough figures covering each municipality as a whole. Check the risk at a specific location on the Hazard Map Portal.",
  aboutAnalytics:
    "This site measures traffic with Google Analytics (GA4) and may serve ads through Google AdSense.",
  aboutLanguageNote:
    "In English, only the interface and metric names are translated. Municipality names and source titles stay in Japanese so that they can be matched against the original datasets.",
  aboutSourcesHeading: (count: number, builtAt: string) =>
    `Sources (${count} municipalities, built at ${builtAt})`,
  aboutContactHeading: "Operator & contact",
  aboutContactBody: "Settle Map is operated by BUILD GEEKS Inc. To report a wrong figure or get in touch, use the ",
  aboutContactBodyAfter: ". How your data is handled is described in the ",
  aboutContactBodyEnd: ".",

  contactTitle: "Contact",
  contactPageTitle: "Contact | Settle Map",
  contactPageDescription:
    "Contact form for Settle Map: data corrections, official akiya bank URLs for a municipality, and anything else.",
  contactIntro:
    "Use this form for data corrections, to send an official akiya (vacant home) bank URL, or for anything else. Replies go to the email address you enter here.",
  contactPrivacyNoteBefore: "How the data you enter is handled is described in the ",
  contactPrivacyNoteAfter: ".",
  contactNameLabel: "Name (optional)",
  contactEmailLabel: "Email address (required)",
  contactTopicLabel: "Topic",
  contactTopicLabels: {
    correction: "Data correction",
    "akiya-bank-url": "Official akiya bank URL",
    other: "Something else",
  },
  contactMessageLabel: "Message (required)",
  contactMessageHint: "Between 10 and 4,000 characters.",
  contactSubmit: "Send",
  contactErrorsHeading: "Please check what you entered",
  contactErrNameTooLong: "The name must be 100 characters or fewer.",
  contactErrEmailRequired: "Enter an email address.",
  contactErrEmailInvalid: "That email address does not look valid.",
  contactErrEmailTooLong: "The email address must be 254 characters or fewer.",
  contactErrTopicInvalid: "Choose a topic.",
  contactErrMessageTooShort: "The message must be at least 10 characters.",
  contactErrMessageTooLong: "The message must be 4,000 characters or fewer.",
  contactErrTurnstile: "The verification failed. Reload the page and try again.",
  contactThanksTitle: "Message sent",
  contactThanksBody:
    "Your message has been received. If it needs a reply, it will go to the email address you entered.",
  contactBackToTop: "Back to the map",
  contactUnavailableTitle: "The contact form is temporarily unavailable",
  contactUnavailableBody:
    "Email delivery is not configured yet, so messages cannot be accepted right now. Please try again later.",
  contactRateLimitedTitle: "Too many messages",
  contactRateLimitedBody: "Please wait a while and try again.",
  contactFailedTitle: "The message could not be sent",
  contactFailedBody: "Sending the email failed. Please try again in a little while.",

  privacyTitle: "Privacy policy",
  privacyPageTitle: "Privacy policy | Settle Map",
  privacyPageDescription:
    "What Settle Map collects, why, who it is shared with, how to opt out, and how long it is kept.",
  privacyUpdated: (date: string) => `Last updated ${date}`,
  privacyOperatorHeading: "Operator",
  privacyOperatorBody: "Settle Map is operated by BUILD GEEKS Inc. To get in touch, use the ",
  privacyOperatorBodyAfter: ".",
  privacyCollectHeading: "What is collected",
  privacyCollectGa:
    "Google Analytics (GA4) cookies, device and browser information, the pages you view, and your IP address. The IP address is anonymised by Google; this site never sees an individual IP address.",
  privacyCollectAds: "Cookies that Google AdSense uses to serve ads.",
  privacyCollectContact:
    "The name, email address, topic and message you enter in the contact form.",
  privacyCollectLocal:
    "Saved candidates and the theme setting are kept in your browser's localStorage and are never sent to the server.",
  privacyPurposeHeading: "Why it is collected",
  privacyPurposeBody:
    "To improve the site (understanding which pages and features are used) and to answer enquiries.",
  privacyThirdPartyHeading: "Third parties",
  privacyThirdPartyBody:
    "Google LLC provides the analytics (GA4) and the ads (AdSense); Resend, Inc. delivers the contact form email. Their own policies describe what they do with the data.",
  privacyLinkGooglePartner: "How Google uses information from sites or apps that use its services",
  privacyLinkGoogleAds: "Google advertising policies",
  privacyLinkResend: "Resend privacy policy",
  privacyOptOutHeading: "Opting out",
  privacyOptOutBody: "Measurement and ad personalisation can be turned off from these pages.",
  privacyLinkGaOptOut: "Google Analytics opt-out browser add-on",
  privacyLinkAdsSettings: "Google ad settings",
  privacyRetentionHeading: "How long it is kept",
  privacyRetentionGa: "GA4 event data is retained for 14 months.",
  privacyRetentionContact:
    "Contact emails are deleted within one year of the enquiry being resolved.",
  privacyDisclaimerHeading: "Disclaimer",
  privacyDisclaimerBefore: "The accuracy of the published data and the disclaimer are covered in ",
  privacyDisclaimerAfter: ".",
  privacyPolicy: "Privacy policy",
  contactNav: "Contact",
};

export const MESSAGES: Record<Lang, Dict> = { ja, en };

export function t(lang: Lang): Dict {
  return MESSAGES[lang];
}
